/**
 * Outlook OAuth Client
 *
 * Uses @ownyou/oauth package for OAuth flow and token management.
 * Extends OutlookClient with OAuth 2.0 flow capabilities.
 *
 * Architecture:
 * - @ownyou/oauth: OAuth flow + token storage
 * - OutlookClient: Low-level Microsoft Graph API calls
 * - OutlookOAuthClient: Integration layer (this file)
 *
 * Usage:
 * 1. const client = new OutlookOAuthClient()
 * 2. await client.authorize() // Redirects to Microsoft OAuth
 * 3. // Callback handled by /oauth/outlook/callback route using @ownyou/oauth
 * 4. const messages = await client.listMessages() // Use Outlook API
 *
 * @see packages/oauth/src/index.ts
 */

import { OutlookClient, type OutlookMessage } from './outlook-client'
import {
  startMicrosoftAuth,
  isMicrosoftConnected,
  getMicrosoftAccessToken,
  disconnectMicrosoft,
} from './oauth-integration'

export interface OutlookOAuthConfig {
  client_id: string
  redirect_uri: string
  scopes: string[]
  auth_uri: string
  token_uri: string
  tenant: string
}

export interface OutlookTokens {
  access_token: string
  refresh_token?: string
  expires_in: number
  token_type: string
  scope: string
  expires_at: number
}

/**
 * Outlook OAuth Client with automatic token management
 *
 * Delegates to @ownyou/oauth package via oauth-integration.ts
 */
export class OutlookOAuthClient {
  private outlookClient: OutlookClient | null = null

  constructor(_userId: string = 'default_user') {
    // userId is now handled by oauth-integration.ts
  }

  /**
   * Initiate Outlook OAuth flow (redirect to Microsoft)
   * Uses @ownyou/oauth package via oauth-integration.ts
   */
  async authorize(): Promise<void> {
    await startMicrosoftAuth()
  }

  /**
   * Handle OAuth callback
   *
   * NOTE: Callback is now handled by /oauth/outlook/callback route
   * using @ownyou/oauth package directly. This method is deprecated.
   */
  async handleCallback(_code: string): Promise<OutlookTokens> {
    // Callback is handled by the callback route using @ownyou/oauth
    // This method should not be called directly anymore
    throw new Error('handleCallback is deprecated - callback is handled by /oauth/outlook/callback route')
  }

  /**
   * Get valid access token (auto-refreshes if needed)
   * Uses @ownyou/oauth package via oauth-integration.ts
   */
  async getValidAccessToken(): Promise<string> {
    return await getMicrosoftAccessToken()
  }

  /**
   * Get initialized Outlook client (with auto token refresh)
   */
  async getClient(): Promise<OutlookClient> {
    const accessToken = await this.getValidAccessToken()

    if (!this.outlookClient) {
      this.outlookClient = new OutlookClient(accessToken)
    } else {
      this.outlookClient.updateAccessToken(accessToken)
    }

    return this.outlookClient
  }

  /**
   * List Outlook messages (with auto token refresh)
   */
  async listMessages(
    filter?: string,
    top: number = 100,
    skip?: number,
    orderBy?: string
  ): Promise<any> {
    const client = await this.getClient()
    return client.listMessages(filter, top, skip, orderBy)
  }

  /**
   * Get single message (with auto token refresh)
   */
  async getMessage(messageId: string, select?: string): Promise<OutlookMessage> {
    const client = await this.getClient()
    return client.getMessage(messageId, select)
  }

  /**
   * Check if user has authorized Outlook access
   * Uses @ownyou/oauth package via oauth-integration.ts
   */
  async isAuthorized(): Promise<boolean> {
    return await isMicrosoftConnected()
  }

  /**
   * Revoke Outlook authorization (clear tokens)
   * Uses @ownyou/oauth package via oauth-integration.ts
   */
  async revokeAuthorization(): Promise<void> {
    await disconnectMicrosoft()
    this.outlookClient = null
  }
}

/**
 * Get OutlookOAuthClient instance (singleton)
 */
let _outlookOAuthClient: OutlookOAuthClient | null = null

export function getOutlookOAuthClient(userId: string = 'default_user'): OutlookOAuthClient {
  if (!_outlookOAuthClient) {
    _outlookOAuthClient = new OutlookOAuthClient(userId)
  }
  return _outlookOAuthClient
}
